/**
 * MCPs tab — toggle MCP servers for this agent.
 *
 * v1 scope: one well-known MCP (claude-peers). Adding custom ones is
 * done by hand-editing settings.json for now.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Plug, Loader2, AlertTriangle, RotateCw } from 'lucide-react';
import type { AgentConfig } from '@/api/agents';
import { mcpsApi, type MCPServerInfo } from '@/api/mcps';
import { useAgentAction } from '@/hooks/useAgents';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { ApiError } from '@/api/client';

interface MCPsProps {
  agent: AgentConfig;
}

export default function MCPs({ agent }: MCPsProps) {
  const qc = useQueryClient();
  const action = useAgentAction();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['mcps', agent.name],
    queryFn: () => mcpsApi.list(agent.name),
    staleTime: 5_000,
  });

  const toggle = useMutation({
    mutationFn: ({ mcpName, enabled }: { mcpName: string; enabled: boolean }) =>
      mcpsApi.toggle(agent.name, mcpName, enabled),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['mcps', agent.name] });
      setNotice(
        `${vars.mcpName} ${vars.enabled ? 'enabled' : 'disabled'}. Restart the agent to apply.`,
      );
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.detail ?? err.title : 'Toggle failed');
      setNotice(null);
    },
  });

  async function handleToggle(mcp: MCPServerInfo, next: boolean) {
    setPending(true);
    try {
      await toggle.mutateAsync({ mcpName: mcp.name, enabled: next });
    } finally {
      setPending(false);
    }
  }

  async function handleRestart() {
    setNotice(null);
    setError(null);
    try {
      await action.mutateAsync({ name: agent.name, action: 'restart' });
      setNotice('Agent restarted. MCP changes are now live.');
    } catch (err) {
      setError(err instanceof ApiError ? err.detail ?? err.title : 'Restart failed');
    }
  }

  if (query.isLoading) {
    return <LoadingSkeleton className="h-40 w-full" />;
  }

  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load MCP servers: {(query.error as Error).message}
      </p>
    );
  }

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">MCP Servers</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Model Context Protocol servers extend this agent with external tools.
            Changes require a restart to take effect.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRestart}
          disabled={action.isPending}
        >
          {action.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RotateCw className="h-4 w-4" />
          )}
          Restart agent
        </Button>
      </div>

      {notice && (
        <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm">
          {notice}
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-3">
        {items.map((mcp) => (
          <Card key={mcp.name}>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0 pb-3">
              <div className="flex gap-3 min-w-0 flex-1">
                <Plug className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    {mcp.display_name}
                    {!mcp.known && (
                      <span className="text-xs font-normal text-muted-foreground">(custom)</span>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">{mcp.description}</CardDescription>
                  {mcp.repo && (
                    <a
                      href={mcp.repo}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                    >
                      {mcp.repo.replace(/^https?:\/\//, '')}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">
                  {mcp.enabled ? 'enabled' : 'disabled'}
                </span>
                <Switch
                  checked={mcp.enabled}
                  disabled={pending || !mcp.known}
                  onCheckedChange={(next) => handleToggle(mcp, next)}
                  aria-label={`Toggle ${mcp.display_name}`}
                />
              </div>
            </CardHeader>
            {!mcp.known && mcp.enabled && (
              <CardContent className="text-xs text-muted-foreground pt-0">
                Custom MCPs can be disabled here but not re-enabled — edit
                <code className="mx-1 font-mono">settings.json</code>
                on the server to restore.
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
